/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react"

import { useDispatch } from "react-redux"

import type { IAppDispatch } from "@/3514/store"
import type { TEmptyCallback } from "@/shared/types/functions"

import {
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatConversationStatus as EConversationStatus,
    type TCoachingChatConversation as TConversation,
    type TCoachingChatConversationErrorPayload as TConversationErrorPayload,
    type TCoachingChatConversationMessage as TConversationMessage,
    type TCoachingChatFormError as TFormError,
    type TCoachingChatSeedQuestionFormField as TQuestionFormField,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"

import { coachingChatActions as actions } from "./coaching-chat.slice"

type _TDispatcher = {
    init: TEmptyCallback
    clear: TEmptyCallback
}

type _TConversationDispatcher = {
    setConversationId(id: number): void
    loadConversation(conversation: TConversation, status?: EConversationStatus): void
    loadHistory(messages: TConversationMessage[]): void
    /**
     * @deprecated considered as legacy; as messages are now renderer by clientMessageId only, no need to make a merge with sort;
     * later on it might be needed to explicitly sort messages by their ids, but for this purpose createEntityAdapter is better;
     * leaving this code as a reminder for this
     *
     * UPD 06/15/2026: issue 6749 loads a lot of messages, so this code is used as a temporary solution to load
     * filtered messages and ids per single operation
     */
    populateMessages(messages: TConversationMessage[]): void
    populateSummary(summary: TSummaryCard[]): void
    updateLastMessage(message: Partial<TConversationMessage>): void
    markInput({ isDimmed, isDisabled }: { isDimmed: boolean; isDisabled: boolean }): void
    setInputValueRaw(value: string): void
    addMessage(message: TConversationMessage): void
    replaceTempMessage(messageId: number, clientMessageId: string): void
    removeMessagesRight(count?: number): void
    removeMessageByClientId(id: string): void
    changeStatus(status: EConversationStatus): void
    triggerError(variant: EConversationErrorVariant, payload?: TConversationErrorPayload): void
    clearError: TEmptyCallback
    completeMatrixFeedback: TEmptyCallback
}

type _TFormDispatcher = {
    loadSeeds(questions: TQuestionFormField[]): void
    populateAnswer(payload: Pick<TQuestionFormField, "id" | "answer">): void
    submitForm: TEmptyCallback
    setFormFilling(isFilling?: boolean): void
    triggerError(error: TFormError): void
    clearError: TEmptyCallback
}

function useDispatcher(entityId: number): ReturnType<(entityId: number) => _TDispatcher> {
    const dispatch: IAppDispatch = useDispatch<IAppDispatch>()

    return useMemo(
        (): _TDispatcher => ({
            init: (): void => void dispatch(actions.initChat({ entityId })),
            clear: (): void => void dispatch(actions.clearChat(entityId))
        }),
        [entityId]
    )
}

function useConversationDispatcher(entityId: number): ReturnType<(entityId: number) => _TConversationDispatcher> {
    const dispatch: IAppDispatch = useDispatch<IAppDispatch>()

    return useMemo(
        (): _TConversationDispatcher => ({
            setConversationId: (conversationId: number): void =>
                void dispatch(actions.setConversationId({ entityId, conversationId })),
            loadConversation: (conversation: TConversation, status?: EConversationStatus): void =>
                void dispatch(actions.loadConversation({ entityId, conversation, status })),
            loadHistory: (messages: TConversationMessage[]): void =>
                void dispatch(actions.loadHistory({ entityId, messages })),
            populateMessages: (messages: TConversationMessage[]): void =>
                void dispatch(actions.populateMessages({ entityId, messages })),
            updateLastMessage: (message: TConversationMessage): void =>
                void dispatch(actions.updateLastMessage({ entityId, message })),
            markInput: ({ isDimmed, isDisabled }: { isDimmed: boolean; isDisabled: boolean }): void =>
                void dispatch(actions.markConversationInput({ entityId, isDimmed, isDisabled })),
            setInputValueRaw: (value: string): void =>
                void dispatch(actions.setConversationInputValueRaw({ entityId, value })),
            populateSummary: (summary: TSummaryCard[]): void =>
                void dispatch(actions.populateSummary({ entityId, summary })),
            addMessage: (message: TConversationMessage): void =>
                void dispatch(actions.addMessage({ entityId, message })),
            replaceTempMessage: (messageId: number, clientMessageId: string): void =>
                void dispatch(actions.replaceTempMessage({ entityId, messageId, clientMessageId })),
            removeMessagesRight: (count?: number): void =>
                void dispatch(actions.removeMessagesRight({ entityId, count })),
            removeMessageByClientId: (id: string): void =>
                void dispatch(actions.removeMessageByClientId({ entityId, id })),
            triggerError: (variant: EConversationErrorVariant, payload?: TConversationErrorPayload): void =>
                void dispatch(actions.triggerConversationError({ entityId, variant, data: payload })),
            clearError: (): void => void dispatch(actions.clearConversationError({ entityId })),
            changeStatus: (status: EConversationStatus): void =>
                void dispatch(actions.changeStatus({ entityId, status })),
            completeMatrixFeedback: (): void => void dispatch(actions.completeMatrixFeedback({ entityId }))
        }),
        [entityId]
    )
}

function useFormDispatcher(entityId: number): ReturnType<(entityId: number) => _TFormDispatcher> {
    const dispatch: IAppDispatch = useDispatch<IAppDispatch>()

    return useMemo(
        (): _TFormDispatcher => ({
            loadSeeds: (questions: TQuestionFormField[]): void =>
                void dispatch(actions.loadSeeds({ entityId, questions })),
            populateAnswer: (question: Pick<TQuestionFormField, "id" | "answer">): void =>
                void dispatch(actions.populateSeed({ entityId, question })),
            submitForm: (): void => void dispatch(actions.submitForm({ entityId })),
            setFormFilling: (value?: boolean): void => void dispatch(actions.setFormFilling({ entityId, value })),
            triggerError: (error: TFormError): void => void dispatch(actions.triggerFormError({ entityId, error })),
            clearError: (): void => void dispatch(actions.clearFormError({ entityId }))
        }),
        [entityId]
    )
}

export {
    useDispatcher as useCoachingChatDispatcher,
    useConversationDispatcher as useCoachingChatConversationDispatcher,
    useFormDispatcher as useCoachingChatFormDispatcher
}
